import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LifeBuoy,
  MessageSquarePlus,
  School,
  User,
  Phone,
  Mail,
  Clock,
  CheckCircle2,
  AlertCircle,
  GraduationCap,
  ChevronRight,
  Inbox,
  RotateCw,
} from "lucide-react";
import { getAdminTeacherSupportList } from "../../../api/crm/teacherSupport/teacherSupportApi";
import { formatDateTimeVN } from "../../../utils/formatUtils";
import { showErrorToast } from "../../../components/ConfirmDialog";
import Pagination from "../../../components/common/Pagination";
import TeacherSupportMiniDashboard from "./TeacherSupportMiniDashboard";
import TeacherSupportFilter from "./TeacherSupportFilter";
import TeacherSupportDetailModal from "./TeacherSupportDetailModal";

const CATEGORY_LABELS = {
  login: "Đăng nhập / thiết bị",
  attendance: "Điểm danh",
  schedule: "Thời khóa biểu",
  materials: "Học liệu / bài giảng",
  account: "Tài khoản",
  other: "Khác",
};

const STATUS_CONFIG = {
  open: {
    label: "Đã gửi",
    className: "bg-amber-50 text-amber-800 border-amber-200",
  },
  in_progress: {
    label: "Đang xử lý",
    className: "bg-blue-50 text-blue-800 border-blue-200",
  },
  resolved: {
    label: "Đã xử lý",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200",
  },
  closed: {
    label: "Đã đóng",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
};

export default function TeacherSupportListPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    keyword: "",
    status: "all",
    request_type: "all",
    category: "all",
    from_date: "",
    to_date: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminTeacherSupportList({
        page: currentPage,
        limit: pageSize,
        keyword: filters.keyword || undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        request_type:
          filters.request_type !== "all" ? filters.request_type : undefined,
        category: filters.category !== "all" ? filters.category : undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
      });

      setItems(Array.isArray(res?.data) ? res.data : []);
      setTotalRecords(res?.pagination?.total || 0);
      if (res?.stats) {
        setStats(res.stats);
      }
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Không tải được danh sách hỗ trợ giáo viên."
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (nextFilters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
  };

  const handleSelectStatus = (statusKey) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === statusKey ? "all" : statusKey,
    }));
    setCurrentPage(1);
  };

  const handleRowClick = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50/30 p-3 pb-24 md:p-8 md:pb-8">
      {/* 1. Page Header (Chuẩn §1.2 Design.md) */}
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <LifeBuoy className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Hỗ trợ & Góp ý <span className="text-red-700">Giáo viên</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Tiếp nhận sự cố kỹ thuật và ý kiến đóng góp từ ứng dụng IMA Lesson
              </p>
            </div>
          </div>
        </div>

        <Link
          to="/crm/teacher_accounts"
          className="hidden shrink-0 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50/90 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-red-800 shadow-sm transition hover:border-red-300 hover:bg-red-100 sm:inline-flex"
        >
          <GraduationCap className="h-4 w-4 text-red-600" strokeWidth={2.25} />
          Tài khoản GV trường
        </Link>
      </div>

      {/* 2. Mini Dashboard (Chuẩn §1.7 Design.md) */}
      <TeacherSupportMiniDashboard
        stats={stats}
        activeStatus={filters.status}
        onSelectStatus={handleSelectStatus}
      />

      {/* 3. Filter Bar (Chuẩn §2 & §3 Design.md) */}
      <TeacherSupportFilter
        filters={filters}
        onChange={handleFilterChange}
        onRefresh={loadData}
        totalRecords={totalRecords}
      />

      {/* 4. Table / Data list (CRM Standard) */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <RotateCw className="h-6 w-6 animate-spin text-red-600" />
            <span className="ml-2.5 text-sm font-medium">Đang tải danh sách phiếu...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 mb-3 shadow-inner">
              <Inbox className="h-6 w-6" />
            </div>
            <h3 className="text-sm font-bold text-gray-800">Không có phiếu hỗ trợ nào</h3>
            <p className="text-xs text-gray-400 max-w-sm mt-1">
              Chưa có phiếu hỗ trợ hoặc ý kiến đóng góp nào phù hợp với bộ lọc hiện tại.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
              <thead className="bg-gray-50/90 border-b border-gray-100">
                <tr className="text-left text-gray-500">
                  <th className="px-5 py-3 text-[11px] font-bold uppercase tracking-wider">
                    Giáo viên & Đơn vị
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider">
                    Loại & Danh mục
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider">
                    Tiêu đề & Nội dung
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wider">
                    Trạng thái
                  </th>
                  <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider">
                    Thời gian
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const isFeedback = item.request_type === "feedback";
                  const statusMeta = STATUS_CONFIG[item.status] || STATUS_CONFIG.open;

                  return (
                    <tr
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleRowClick(item)}
                      onKeyDown={(e) => e.key === "Enter" && handleRowClick(item)}
                      className="cursor-pointer hover:bg-red-50/30 transition-colors"
                    >
                      {/* Cột 1: Giáo viên & Đơn vị */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 border border-red-100 text-red-700 font-black text-xs uppercase shadow-sm">
                            {item.teacher_name
                              ? item.teacher_name
                                  .split(" ")
                                  .map((p) => p[0])
                                  .slice(-2)
                                  .join("")
                              : "GV"}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">
                              {item.teacher_name || "—"}
                            </p>
                            <p className="text-xs text-gray-500 truncate flex items-center gap-1.5 mt-0.5">
                              <School className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                              <span className="truncate">{item.school_name || "Chưa gán trường"}</span>
                            </p>
                            {(item.contact_phone || item.teacher_phone) && (
                              <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                                <Phone className="h-3 w-3 shrink-0" />
                                <span>{item.contact_phone || item.teacher_phone}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Cột 2: Loại & Danh mục */}
                      <td className="px-4 py-3.5">
                        <div className="flex flex-col gap-1 items-start">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                              isFeedback
                                ? "bg-purple-50 text-purple-700 border-purple-200"
                                : "bg-red-50 text-red-700 border-red-200"
                            }`}
                          >
                            {isFeedback ? (
                              <MessageSquarePlus className="h-3 w-3" />
                            ) : (
                              <LifeBuoy className="h-3 w-3" />
                            )}
                            {isFeedback ? "Góp ý" : "Hỗ trợ"}
                          </span>
                          <span className="text-xs text-gray-600 font-medium">
                            {CATEGORY_LABELS[item.category] || item.category}
                          </span>
                        </div>
                      </td>

                      {/* Cột 3: Tiêu đề & Nội dung */}
                      <td className="px-4 py-3.5 max-w-sm">
                        <p className="font-bold text-gray-900 truncate">
                          {item.subject}
                        </p>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5 leading-relaxed">
                          {item.content}
                        </p>
                        {item.admin_note && (
                          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-700 font-medium bg-emerald-50/60 px-2 py-0.5 rounded-md border border-emerald-100 max-w-max">
                            <CheckCircle2 className="h-3 w-3 shrink-0" />
                            <span className="truncate">Phản hồi: {item.admin_note}</span>
                          </div>
                        )}
                      </td>

                      {/* Cột 4: Trạng thái */}
                      <td className="px-4 py-3.5 text-center">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold border ${statusMeta.className}`}
                        >
                          {statusMeta.label}
                        </span>
                      </td>

                      {/* Cột 5: Thời gian */}
                      <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <span>{formatDateTimeVN(item.created_at)}</span>
                        </div>
                        {item.resolved_at && (
                          <p className="text-[11px] text-emerald-600 font-medium mt-1">
                            Xong: {formatDateTimeVN(item.resolved_at)}
                          </p>
                        )}
                      </td>

                      {/* Cột 6: Thao tác */}
                      <td className="px-5 py-3.5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(item);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50/80 px-3.5 py-2 text-xs font-bold text-red-700 shadow-sm hover:border-red-300 hover:bg-red-100 active:scale-95 transition"
                        >
                          Xem & Trả lời
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Phân trang */}
        {totalRecords > 0 && (
          <div className="border-t border-gray-100 p-4">
            <Pagination
              totalRecords={totalRecords}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        )}
      </div>

      {/* Modal chi tiết và trả lời */}
      <TeacherSupportDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        onUpdated={loadData}
      />
    </div>
  );
}
